import {
  Icon as TablerIcon,
  IconBuilding,
  IconBuildingWarehouse,
  IconCamper,
  IconCar,
  IconHome,
  IconHomeDollar,
  IconLockOpen,
  IconMotorbike,
  IconPlane,
  IconShip,
  IconTextScan2,
} from '@tabler/icons-react';
import { isFlagEnabled, useAllFeatureFlags } from 'context/FeatureFlagContext';
import { useMemo } from 'react';
import AssetClassConfig, { AssetClassId } from 'types/assetClass';

export type AssetClassNavEntry = {
  routeSegment: AssetClassId;
  icon: TablerIcon;
  text: string;
  // Not built yet: never selectable, whatever the flags say.
  disabled?: boolean;
  // Built, but gated on this runtime flag. Governs the side nav and the
  // marketplace asset-class filter only — other surfaces gate independently
  // (LoanTapeUploader and PortfolioDashboard read their own flags, and `cre`
  // is gated by `creEnabled` there while declaring nothing here). Off until
  // the flag resolves, so a gated class never flashes in mid-query.
  featureFlag?: string;
  dynamicRouteFn?: (params: Record<string, string>) => string;
};

export const SIDE_NAV_CONFIG: AssetClassNavEntry[] = [
  {
    routeSegment: 'home',
    icon: IconHome,
    text: AssetClassConfig.HOME.label,
  },
  {
    routeSegment: 'auto',
    icon: IconCar,
    text: AssetClassConfig.AUTO.label,
  },
  {
    routeSegment: 'rv',
    icon: IconCamper,
    text: 'RV',
    disabled: true,
  },
  {
    routeSegment: 'unsecuredpersonal',
    icon: IconLockOpen,
    text: AssetClassConfig.UNSECPERSONAL.label,
  },
  {
    routeSegment: 'cre',
    icon: IconBuilding,
    text: AssetClassConfig.CRE.label,
  },
  {
    routeSegment: 'heloc',
    icon: IconHomeDollar,
    text: AssetClassConfig.HELOC.label,
    featureFlag: 'helocEnabled',
  },
  {
    routeSegment: 'aircraft',
    icon: IconPlane,
    text: 'Aircraft',
    disabled: true,
  },
  {
    routeSegment: 'motorcycle',
    icon: IconMotorbike,
    text: 'Motorcycle',
    disabled: true,
  },
  {
    routeSegment: 'warehouse',
    icon: IconBuildingWarehouse,
    text: 'Warehouse',
    disabled: true,
  },
  {
    routeSegment: 'marine',
    icon: IconShip,
    text: 'Marine',
    featureFlag: 'marineEnabled',
  },
  {
    routeSegment: 'revolving',
    icon: IconTextScan2,
    text: 'Revolving',
    disabled: true,
  },
];

// `disabled` and `featureFlag` compose rather than override: an entry that is
// both unfinished and flag-gated stays off even once its flag flips.
export const isNavEntryEnabled = (
  entry: AssetClassNavEntry,
  flags: Record<string, string | null>,
): boolean =>
  !entry.disabled && (entry.featureFlag ? isFlagEnabled(flags[entry.featureFlag]) : true);

export const useEnabledAssetClassNavEntries = (): AssetClassNavEntry[] => {
  const flags = useAllFeatureFlags();

  return useMemo(() => SIDE_NAV_CONFIG.filter((entry) => isNavEntryEnabled(entry, flags)), [flags]);
};
